import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useDialogs } from "./DialogsContext";

const EASE = [0.22, 1, 0.36, 1];

// Masked line reveal — each line slides up from behind a clip.
function MaskLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { openAudit, openBook } = useDialogs();
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative pt-28 lg:pt-36 pb-20 lg:pb-28 overflow-hidden min-h-[92vh] flex flex-col justify-center"
    >
      {/* Parallax grid */}
      <motion.div
        aria-hidden
        style={{ y: gridY }}
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </motion.div>
      <motion.div
        aria-hidden
        style={{ y: glowY }}
        className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full blur-[130px] bg-[#ccff00]/10 pointer-events-none"
      />

      {/* Giant ghost word behind */}
      <motion.span
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 0.6 }}
        className="pointer-events-none select-none absolute -bottom-6 lg:-bottom-16 -left-4 font-display text-[26vw] leading-none uppercase text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.04)]"
      >
        Rescue
      </motion.span>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 relative"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/80 mb-8"
        >
          <span className="w-1.5 h-1.5 bg-[#ccff00] animate-pulse" />
          Results-first database reactivation
        </motion.div>

        <h1 className="font-display text-[46px] sm:text-6xl lg:text-[104px] uppercase max-w-5xl">
          <MaskLine delay={0.05}>No results.</MaskLine>
          <MaskLine delay={0.16}>
            <span className="text-[#ccff00]">No pay.</span>{" "}
            <span className="text-white/40">Period.</span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-8 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed"
        >
          We resurrect the revenue hiding in your dormant database — leads who
          ghosted, customers who lapsed, deals you forgot. You only pay when
          closed sales land in your calendar.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.62 }}
          className="mt-10 flex flex-col sm:flex-row gap-3"
        >
          <button
            data-testid="hero-cta-audit"
            onClick={openAudit}
            className="group inline-flex items-center justify-center gap-2 h-14 px-7 bg-[#ccff00] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#b3e600] transition-all hover:translate-x-0.5"
          >
            Get my free audit
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={3} />
          </button>
          <button
            data-testid="hero-cta-call"
            onClick={openBook}
            className="inline-flex items-center justify-center gap-2 h-14 px-7 border border-white/20 text-white font-semibold uppercase tracking-wider text-sm hover:border-white/60 hover:bg-white/5 transition-all"
          >
            Book a 20-min call
          </button>
        </motion.div>

        {/* Stat strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-20 grid grid-cols-2 lg:grid-cols-4 border-t border-white/10"
        >
          {[
            { k: "$4.2M+", v: "Revenue rescued for clients" },
            { k: "127", v: "Databases reactivated" },
            { k: "9.3×", v: "Avg. ROI in 30 days" },
            { k: "0", v: "Upfront fees. Ever." },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.9 + i * 0.08 }}
              className="border-b sm:border-r border-white/10 p-6 lg:p-8 last:border-r-0"
            >
              <div className="font-display text-3xl lg:text-5xl text-white">
                {s.k}
              </div>
              <div className="mt-2 text-xs uppercase tracking-widest text-white/50">
                {s.v}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
